import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { type KeyedMutator } from 'swr';
import { toast } from '@/components';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { EventEntities } from '@/types/event';
import type { Person, UpdatePersonPayload } from '@/types/person';
import type { UpdateVehiclePayload, Vehicle } from '@/types/vehicle';
import { useDuplicatePlateMerge } from '@/features/VehicleGrid/hooks/useDuplicatePlateMerge';

/**
 * Shared person/vehicle edit state and save flow for the EventView entity
 * components, keeping `EventEntitiesGrid` and `EventLinkedEntityPanel` in sync.
 */
export function useEventEntityEditors(mutate: KeyedMutator<EventEntities>) {
    const { t: tDirectory } = useTranslation(I18nNamespace.DIRECTORY);
    const [editPersonId, setEditPersonId] = useState<string | null>(null);
    const [editVehicleId, setEditVehicleId] = useState<string | null>(null);

    const { data: editPerson } = useSWR<Person>(
        editPersonId ? `person-${editPersonId}` : null,
        () => EngineService.getPerson(editPersonId as string),
    );
    const { data: editVehicle } = useSWR<Vehicle>(
        editVehicleId ? `vehicle-${editVehicleId}` : null,
        () => EngineService.getVehicle(editVehicleId as string),
    );

    const { handleUpdateError, dialog: duplicatePlateDialog } =
        useDuplicatePlateMerge({
            onMerged: () => {
                void mutate();
                setEditVehicleId(null);
            },
            onSourceStale: () => {
                void mutate();
                setEditVehicleId(null);
            },
        });

    const handleUpdatePerson = async (
        personId: string,
        payload: UpdatePersonPayload,
    ): Promise<boolean> => {
        try {
            await EngineService.updatePerson(personId, payload);
        } catch {
            toast.error(tDirectory('persons.editModal.error'));
            return false;
        }
        void mutate();
        setEditPersonId(null);
        return true;
    };

    const handleUpdateVehicle = async (
        vehicleId: string,
        payload: UpdateVehiclePayload,
    ): Promise<boolean> => {
        try {
            await EngineService.updateVehicle(vehicleId, payload);
        } catch (error) {
            if (!handleUpdateError(error, vehicleId)) {
                toast.error(tDirectory('vehicles.editModal.error'));
            }
            return false;
        }
        void mutate();
        setEditVehicleId(null);
        return true;
    };

    return {
        editPerson,
        editVehicle,
        editPersonId,
        editVehicleId,
        setEditPersonId,
        setEditVehicleId,
        handleUpdatePerson,
        handleUpdateVehicle,
        duplicatePlateDialog,
    };
}
